/**
 * Lore Mention utilities.
 *
 * The Mention extension is configured inline in chapter-editor.tsx using
 * @tiptap/extension-mention directly. This module only exports the helper
 * function to extract mention IDs from Tiptap JSON.
 */

/** Default character budget for mention hover-card snippets. */
export const SNIPPET_MAX_CHARS = 1000;

/**
 * Walk Tiptap JSON content and return a plain-text snippet, capped at `max`
 * characters. Inserts a single space between block boundaries and collapses
 * whitespace runs so the output is compact.
 */
export function extractSnippet(
    content: unknown,
    max: number = SNIPPET_MAX_CHARS
): string {
    if (!content || typeof content !== "object") return "";
    if (max <= 0) return "";

    let out = "";

    function walk(node: Record<string, unknown>): boolean {
        if (typeof node.text === "string") {
            out += node.text;
            if (out.length >= max) return true;
        }
        if (Array.isArray(node.content)) {
            for (const child of node.content) {
                if (walk(child as Record<string, unknown>)) return true;
            }
            if (out.length > 0 && !out.endsWith(" ")) out += " ";
        }
        return false;
    }

    walk(content as Record<string, unknown>);
    out = out.replace(/\s+/g, " ").trim();
    if (out.length > max) out = out.slice(0, max).trimEnd() + "…";
    return out;
}

/**
 * Extract all mention entry IDs from Tiptap JSON content.
 * Walks the document tree and collects unique IDs from "mention" nodes.
 */
export function extractMentionIds(content: Record<string, unknown>): string[] {
    const ids = new Set<string>();

    function walk(node: Record<string, unknown>) {
        if (node.type === "mention" && typeof node.attrs === "object" && node.attrs !== null) {
            const attrs = node.attrs as Record<string, unknown>;
            if (typeof attrs.id === "string" && attrs.id) {
                ids.add(attrs.id);
            }
        }
        if (Array.isArray(node.content)) {
            for (const child of node.content) {
                walk(child as Record<string, unknown>);
            }
        }
    }

    walk(content);
    return [...ids];
}

/** Compare mention IDs as sets (order and duplicate nodes are irrelevant). */
export function sameMentionSets(left: string[], right: string[]): boolean {
    const leftSet = new Set(left);
    const rightSet = new Set(right);
    if (leftSet.size !== rightSet.size) return false;
    for (const id of leftSet) {
        if (!rightSet.has(id)) return false;
    }
    return true;
}
